import React, { Suspense, useEffect, useMemo, useRef } from 'react'

import type { CarouselProps } from '@awazevr/design-system.components.carousel'
import type { IPropertyCard } from '@awazevr/design-system.compositions.property-card'
import { breakpoints, useWindowWidth } from '@awazevr/design-system.utils.react'
import { useIntlContext } from '@awazevr/mfe-common.contexts.intl'

import {
  PropertiesNearbyCarouselWrapper,
  PropertiesNearbyContainer,
  PropertiesNearbyHeader,
  PropertiesNearbyHeading,
  ViewMoreButton,
} from './PropertiesNearby.style'

// Lazy-loaded: the shared carousel ships ESM and breaks the sync import graph.
const PropertyCardCarousel = React.lazy(() =>
  import('@awazevr/design-system.compositions.property-card-carousel').then(
    (module) => ({ default: module.PropertyCardCarousel }),
  ),
)

const MAX_CARDS = 8
const CONTROLLER_ID = 'properties-nearby'
// With only 1-2 cards the carousel would stretch them across the full track
// (a lone card skips Swiper entirely), so cap the rendered width to the cards'
// natural size and centre them.
const CARD_MAX_WIDTH = 341
const CARD_GAP = 24

export interface PropertiesNearbyProps {
  properties: IPropertyCard[]
  onViewMore: () => void
  onCardClick: () => void
  onSeen: () => void
}

export const PropertiesNearby = ({
  properties,
  onViewMore,
  onCardClick,
  onSeen,
}: PropertiesNearbyProps): JSX.Element | null => {
  const { formatMessage } = useIntlContext()
  const width = useWindowWidth() ?? 0
  const isMobile = width > 0 && width < breakpoints[0]

  // Key the "seen" guard on the visible set, not a one-shot flag: this component
  // doesn't unmount on SPA filter changes, so a new set of expanded properties
  // must re-fire the impression (mirrors the low-results re-fire in
  // SearchResults). Same set re-rendering does not.
  const seenKey = useMemo(
    () =>
      properties
        .slice(0, MAX_CARDS)
        .map((property) => property.code)
        .join('|'),
    [properties],
  )
  const lastSeenKeyRef = useRef<string | null>(null)

  useEffect(() => {
    if (properties.length === 0 || seenKey === lastSeenKeyRef.current) {
      return
    }
    lastSeenKeyRef.current = seenKey
    onSeen()
  }, [onSeen, seenKey, properties.length])

  const cards = useMemo(
    () =>
      properties.slice(0, MAX_CARDS).map((property) => ({
        ...property,
        onPropertyLinkClick: () => {
          onCardClick()
          property.onPropertyLinkClick?.()
        },
      })),
    [properties, onCardClick],
  )

  if (properties.length === 0) {
    return null
  }

  const cardCount = cards.length
  const fewCards = cardCount <= 2
  // We trim to MAX_CARDS for display. When the source has more, the link is
  // "View more"; when we're already showing them all it's "View all" (the
  // expanded page still adds value — filtering, sorting, a full grid).
  const hasMoreThanShown = properties.length > MAX_CARDS
  // Centre 1-2 cards at their natural size instead of stretching them.
  const fewCardsMaxWidth =
    cardCount * CARD_MAX_WIDTH + (cardCount - 1) * CARD_GAP

  const carouselProps = {
    children: <span />,
    loop: false,
    hideNavButtons: isMobile,
    swiperOptions: {
      // Fractional slidesPerView gives the peek. Sits inside a definite
      // grid-track width, so Swiper's set-wrapper-size stays bounded.
      // Mobile advances 1 card at a time; tablet/desktop advance 2.
      slidesPerView: 1.2,
      slidesPerGroup: 1,
      spaceBetween: CARD_GAP,
      ...(width >= breakpoints[0] && {
        // Cap to the card count so 2 cards fill the track without an empty peek.
        slidesPerView: Math.min(cardCount, 2.5),
        slidesPerGroup: 2,
      }),
      keyboard: false,
      controller: {
        control: CONTROLLER_ID,
      },
    },
    customPagination: {
      type: 'bullets',
      dynamicBullets: false,
      dynamicMainBullets: 1,
    },
    translations: {
      previousNavigation: formatMessage({
        id: 'property-carousel.previousNavigation',
        defaultMessage: 'Previous slide',
      }),
      nextNavigation: formatMessage({
        id: 'property-carousel.nextNavigation',
        defaultMessage: 'Next slide',
      }),
    },
  } satisfies CarouselProps

  const heading = formatMessage({
    id: 'search-results.properties-nearby.heading',
    defaultMessage: 'Properties nearby',
  })

  return (
    <PropertiesNearbyContainer
      aria-label={heading}
      data-testid="properties-nearby"
    >
      <PropertiesNearbyHeader>
        <PropertiesNearbyHeading>{heading}</PropertiesNearbyHeading>
        <ViewMoreButton
          type="button"
          onClick={onViewMore}
          data-testid="properties-nearby-view-more"
        >
          {formatMessage(
            hasMoreThanShown
              ? {
                  id: 'search-results.properties-nearby.view-more',
                  defaultMessage: 'View more',
                }
              : {
                  id: 'search-results.properties-nearby.view-all',
                  defaultMessage: 'View all',
                },
          )}
        </ViewMoreButton>
      </PropertiesNearbyHeader>

      <PropertiesNearbyCarouselWrapper
        style={
          fewCards
            ? {
                maxWidth: fewCardsMaxWidth,
                marginLeft: 'auto',
                marginRight: 'auto',
              }
            : undefined
        }
      >
        <Suspense fallback={null}>
          <PropertyCardCarousel
            carouselProps={carouselProps}
            propertyCardsData={cards}
          />
        </Suspense>
      </PropertiesNearbyCarouselWrapper>
    </PropertiesNearbyContainer>
  )
}
